/* eslint-disable max-len */
import PropTypes from 'prop-types'

import { DeviceTypes } from '../../models/drivers/device-types-enum.js'
import { DriverRoles } from '../../models/drivers/driver-roles-enum.js'
import { idPropType } from '../common/id-prop-type'
import { iso8601PropType } from '../common/iso-8601-prop-type'
import { locationDescriptionPropType } from '../common/location-description-prop-type'
import { positionPropType } from '../common/position-prop-type'
import { workStatePropType } from './work-state-prop-type'
import { workingTimesActivityEditPropType } from './working-times-activity-edit-prop-type'

const deviceTypePropType = PropTypes.oneOf(Object.values(DeviceTypes))

/**
 * @typedef {Object} WorkingTime
 * @property {Id} @deprecated           [workingTimeIntervalId]     - The id of the working time interval (from Report Engine API, deprecated)
 * @property {String}                   [globalId]                  - Unique id for all activities (regular ones and new ones added by the user)
 * @property {Id}                       [driverId]                  - The ID of the driver.
 * @property {String}                   [driverRole]
 * @property {Id}                       [endAddressId]              - The ID of the address at the endpoint of the working time interval.
 * @property {String}                   [endExternalDeviceType]     - Device that triggered work state change that ended the activity
 * @property {LocationDescription}      [endLocationDescription]    - The position description of the endpoint of the working time interval.
 * @property {number}                   [endOdometer]               - The odometer of the object at the endpoint of the working time interval (in meters).
 * @property {Position}                 [endPosition]               - The geo position of the endpoint of the working time interval (decimal degree).
 * @property {ISO8601}                  [endTime]                   - The end date of the interval (ISO8601).
 * @property {Id}                       [objectId]                  - The ID of the object the driver was assigned to when starting the activity
 * @property {Id}                       [endObjectId]               - The ID of the object the driver was assigned to when ending the activity
 * @property {Id}                       [startAddressId]            - The ID of the address at the starting point of the working time interval.
 * @property {String}                   [startExternalDeviceType]   - Device that triggered work state change that started the activity
 * @property {LocationDescription}      [startLocationDescription]  - The location description of the starting point of the working time interval.
 * @property {number}                   [startOdometer]             - The odometer of the object at the beginning of the working time interval (in meters).
 * @property {Position}                 [startPosition]             - The geo position of the starting point of the working time interval (decimal degree).
 * @property {ISO8601}                  [startTime]                 - The start date of the interval (ISO8601).
 * @property {WorkStates}               [workState]                 - The work state for this interval.
 * @property {String} @deprecated       [workStateEventSource]      - Device that triggered work state change that started the activity. (Will be replaced by 'startExternalDeviceType' in new API.)
 * @property {number}                   [externalDeviceTimeOffset]  - Difference between timestamps of the reporting device and the device that triggered work state change
 * @property {WorkingTimesActivityEdit} [editInfo]                  - Meta edit info, available if the activity was edited by a user
 */
export const workingTimePropType = PropTypes.shape({
    workingTimeIntervalId: idPropType /* deprecated */,
    globalId: PropTypes.string,
    driverId: idPropType,
    driverRole: PropTypes.oneOf(Object.values(DriverRoles)),
    objectId: idPropType,
    endObjectId: idPropType,
    endAddressId: idPropType,
    endExternalDeviceType: deviceTypePropType,
    endLocationDescription: locationDescriptionPropType,
    endOdometer: PropTypes.number,
    endPosition: positionPropType,
    endTime: iso8601PropType,
    startAddressId: idPropType,
    startExternalDeviceType: deviceTypePropType,
    startLocationDescription: locationDescriptionPropType,
    startOdometer: PropTypes.number,
    startPosition: positionPropType,
    startTime: iso8601PropType,
    workState: workStatePropType,
    workStateEventSource: deviceTypePropType /* deprecated */,
    externalDeviceTimeOffset: PropTypes.number,
    editInfo: workingTimesActivityEditPropType
})
